import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiFootballService,
  type ApiFootballAuditResponse,
  type ApiFootballIngestSummary,
} from "@/api/services/apiFootball.service";

export const apiFootballKeys = {
  all: ["api-football"] as const,
  audit: () => [...apiFootballKeys.all, "audit"] as const,
};

/**
 * Runs the coverage audit. Disabled by default — caller flips `enabled`
 * to true (e.g. on a button click) so the page doesn't auto-call the
 * backend on every mount; the audit costs one /fixtures/events API call
 * per completed match (~100 calls during knockout stage).
 */
export function useApiFootballAudit(enabled = false) {
  return useQuery<ApiFootballAuditResponse>({
    queryKey: apiFootballKeys.audit(),
    queryFn: apiFootballService.audit,
    enabled,
    // Stale immediately so re-clicking the button does a fresh fetch.
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Re-ingest a single match by id. Idempotent (the MatchEvent unique
 * constraint dedupes), so safe to call multiple times on the same match.
 * On success, invalidates the audit so the gap row reflects the fix.
 */
export function useApiFootballReingest() {
  const qc = useQueryClient();
  return useMutation<ApiFootballIngestSummary, Error, number>({
    mutationFn: (matchId) => apiFootballService.ingestMatch(matchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: apiFootballKeys.audit() });
    },
  });
}

/**
 * Fire the same 5-min cron tick on demand. Use to force an immediate
 * sweep of all pollable matches instead of waiting for the next
 * scheduled fire. Server-side logs the work as
 * `[ApiFootballScheduler] tick start (manual)`.
 */
export function useApiFootballTickNow() {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; note: string }, Error, void>({
    mutationFn: () => apiFootballService.tickNow(),
    onSuccess: () => {
      // Audit reflects any new events the tick wrote.
      qc.invalidateQueries({ queryKey: apiFootballKeys.audit() });
    },
  });
}
